import moment from 'moment-timezone';
import { useMemo } from 'react';
import {
  calculateChange,
  calculateHighs,
  calculateLows
} from '../../../utils/bars';
import useLocalize from '../../../utils/hooks/useLocalize';
import Error from '../../common/Error';
import Loading from '../../common/Loading';
import NoData from '../../common/NoData';
import Number from '../../common/Number';

const mdash = '\u2014';

// Helper to check if bar size uses aggregated data
const isAggregatedBarSize = (barSize) =>
  ['1month', '1week', '1quarter'].includes(barSize);

const HistoricalTable = ({
  bars,
  barSize,
  precision,
  isLoading,
  isError,
  noData,
  isSymbolEquity,
  previousVolume
}) => {
  const localize = useLocalize();

  const getFormattedDate = (bar) => {
    if (barSize === '1month' || barSize === '1quarter') {
      // Subtract 4 days to align timestamp to beginning of the period for display
      return moment(bar.Timestamp).subtract(4, 'd').format('YYYY/\u200bMM');
    } else if (barSize === '1week') {
      const date = new Date(bar.Timestamp);
      const dayOfWeek = date.getDay();
      const offset = dayOfWeek === 0 ? -6 : dayOfWeek - 1;
      const newDay = date.getDate() - offset;
      const weekStart = new Date(date.setDate(newDay));
      const timestampBits = weekStart.toISOString().split('T');
      return moment(timestampBits[0]).format('YYYY/\u200bMM/DD');
    } else if (barSize === '5min' || barSize === '1hour') {
      return moment(bar.Timestamp).tz('Asia/Tokyo').format('MM/DD HH:mm');
    } else {
      // 1 day
      const timestampBits = bar.Timestamp.split('T');
      return moment(timestampBits[0]).format('YYYY/\u200bMM/DD');
    }
  };

  const shouldDisplayVolume = isSymbolEquity || previousVolume > 0;

  const getVolumeValue = (bar) => {
    if (isAggregatedBarSize(barSize)) {
      return bar.AverageDailyVolume;
    }
    return bar.Volume;
  };

  const volumeField = isAggregatedBarSize(barSize)
    ? 'AverageDailyVolume'
    : 'Volume';

  // Calculate period high and low values for each field
  // Memoize to avoid recalculation on every render
  // Must be called before any conditional returns (Rules of Hooks)
  const { barsWithChange, periodHighs, periodLows } = useMemo(() => {
    if (!bars || bars.length === 0) {
      return {
        barsWithChange: [],
        periodHighs: null,
        periodLows: null
      };
    }
    const withChange = calculateChange(bars);
    return {
      barsWithChange: withChange,
      periodHighs: calculateHighs(withChange),
      periodLows: calculateLows(withChange)
    };
  }, [bars]);

  if (noData) {
    return <NoData />;
  } else if (isLoading) {
    return <Loading />;
  } else if (isError) {
    return <Error />;
  }

  if (!bars || bars.length === 0) {
    return <NoData />;
  }

  return (
    <div className="historical-table">
      <div className="historical-table-header">
        <div className="historical-table-cell">
          {localize(
            barSize === '5min' || barSize === '1hour'
              ? 'symbol.details.historical.header.time'
              : barSize === '1month' || barSize === '1quarter'
              ? 'symbol.details.historical.header.month'
              : 'symbol.details.historical.header.date'
          )}
        </div>
        <div className="historical-table-cell">
          {localize('symbol.details.historical.header.open')}
        </div>
        <div className="historical-table-cell">
          {localize('symbol.details.historical.header.high')}
        </div>
        <div className="historical-table-cell">
          {localize('symbol.details.historical.header.low')}
        </div>
        <div className="historical-table-cell">
          {localize('symbol.details.historical.header.close')}
        </div>
        <div className="historical-table-cell">
          {localize(`symbol.details.historical.header.change.${barSize}`)}
        </div>
        {shouldDisplayVolume && (
          <div className="historical-table-cell">
            {localize(
              barSize === '1month' ||
                barSize === '1week' ||
                barSize === '1quarter'
                ? 'symbol.details.historical.header.averagevolume'
                : 'symbol.details.historical.header.volume'
            )}
          </div>
        )}
      </div>
      <div className="historical-table-body">
        {barsWithChange.map((bar, index) => {
          const change = bar.changeAmount;
          const changeClass =
            change === null || change === 0
              ? ''
              : change >= 0
              ? ' positive'
              : ' negative';

          // Helper function to check if a value is valid and matches the period value
          const isValidAndEqual = (value, periodValue) => {
            return (
              value !== null && value !== undefined && value === periodValue
            );
          };

          // Get highlighting flags for this bar
          const volumeValue = getVolumeValue(bar);
          const isOpenHigh = isValidAndEqual(
            bar.OpenPrice,
            periodHighs.OpenPrice
          );
          const isOpenLow = isValidAndEqual(
            bar.OpenPrice,
            periodLows.OpenPrice
          );
          const isHighHigh = isValidAndEqual(
            bar.HighPrice,
            periodHighs.HighPrice
          );
          const isHighLow = isValidAndEqual(
            bar.HighPrice,
            periodLows.HighPrice
          );
          const isLowHigh = isValidAndEqual(bar.LowPrice, periodHighs.LowPrice);
          const isLowLow = isValidAndEqual(bar.LowPrice, periodLows.LowPrice);
          const isCloseHigh = isValidAndEqual(
            bar.ClosePrice,
            periodHighs.ClosePrice
          );
          const isCloseLow = isValidAndEqual(
            bar.ClosePrice,
            periodLows.ClosePrice
          );
          const isVolumeHigh = volumeValue === periodHighs[volumeField];
          const isVolumeLow = volumeValue === periodLows[volumeField];
          const isChangeHigh = bar.changeAmount === periodHighs.changeAmount;
          const isChangeLow = bar.changeAmount === periodLows.changeAmount;

          return (
            <div
              key={index}
              className={`historical-table-row${
                index % 2 === 1 ? ' alternate' : ''
              }`}
            >
              <div className="historical-table-cell">
                {getFormattedDate(bar)}
              </div>
              <div
                className={`historical-table-cell${
                  isOpenHigh ? ' period-high' : ''
                }${isOpenLow ? ' period-low' : ''}`}
              >
                {bar.OpenPrice !== undefined && bar.OpenPrice !== null ? (
                  <Number value={bar.OpenPrice} precision={precision} />
                ) : (
                  mdash
                )}
              </div>
              <div
                className={`historical-table-cell${
                  isHighHigh ? ' period-high' : ''
                }${isHighLow ? ' period-low' : ''}`}
              >
                {bar.HighPrice !== undefined && bar.HighPrice !== null ? (
                  <Number value={bar.HighPrice} precision={precision} />
                ) : (
                  mdash
                )}
              </div>
              <div
                className={`historical-table-cell${
                  isLowHigh ? ' period-high' : ''
                }${isLowLow ? ' period-low' : ''}`}
              >
                {bar.LowPrice !== undefined && bar.LowPrice !== null ? (
                  <Number value={bar.LowPrice} precision={precision} />
                ) : (
                  mdash
                )}
              </div>
              <div
                className={`historical-table-cell${
                  isCloseHigh ? ' period-high' : ''
                }${isCloseLow ? ' period-low' : ''}`}
              >
                {bar.ClosePrice !== undefined && bar.ClosePrice !== null ? (
                  <Number value={bar.ClosePrice} precision={precision} />
                ) : (
                  mdash
                )}
              </div>
              <div
                className={`historical-table-cell${changeClass}${
                  isChangeHigh ? ' period-high' : ''
                }${isChangeLow ? ' period-low' : ''}`}
              >
                {change !== null ? (
                  <>
                    {change >= 0 ? '+' : ''}
                    <Number value={change} precision={precision} />
                  </>
                ) : (
                  mdash
                )}
              </div>
              {shouldDisplayVolume && (
                <div
                  className={`historical-table-cell${
                    isVolumeHigh ? ' period-high' : ''
                  }${isVolumeLow ? ' period-low' : ''}`}
                >
                  {volumeValue !== undefined && volumeValue !== null ? (
                    <Number value={volumeValue} precision={0} />
                  ) : (
                    mdash
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HistoricalTable;
